import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ---------- Types ----------

export type EdiPartnerStatus = 'onboarding' | 'testing' | 'live' | 'suspended';

export interface GoLiveChecklist {
  cdataConnected: boolean;
  mappingsValidated: boolean;
  testFileApproved: boolean;
  waiConnected: boolean;
}

/**
 * FourKites per-partner config (stored on EdiTradingPartner.fourkitesConfig).
 * The partner's top-level `scacCode` doubles as the FourKites SCAC, so it is
 * intentionally NOT duplicated here. Accepts the V2 keys plus the legacy V1
 * `edi_fourkites_*` keys for back-compat on read.
 */
export interface FourkitesConfig {
  username?: string;
  password?: string;
  /** 'Tracking Information' | 'Load status' */
  events?: string[];
  /** minutes, 2–60 */
  eventInterval?: number;
  /** 'Truck' | 'Trailer' */
  trackingFrom?: string;
  // legacy V1 keys (read-only back-compat)
  edi_fourkites_username?: string;
  edi_fourkites_password?: string;
  baseUrl?: string;
}

export interface EdiPartner {
  id: string;
  name: string;
  scacCode: string;
  /** V1 tri-state: 0 = No Response, 1 = Accept, 2 = Reject */
  automaticResponse204: number;
  autoSend210: boolean;
  autoTrigger214OnGeofence: boolean;
  fourkitesEnabled: boolean;
  fourkitesConfig?: FourkitesConfig | null;
  blueYonderEnabled: boolean;
  ediPlusEnabled: boolean;
  partnerStatus: EdiPartnerStatus;
  testMode: boolean;
  goLiveChecklist: GoLiveChecklist;
  receiverIds: Array<{ id: string; name: string; states: string[] }>;
  receiverStateMapping: Record<string, string>;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EdiTransaction {
  id: string;
  partnerId: string;
  partnerName?: string;
  transactionSet: '204' | '990' | '214' | '210';
  direction: 'inbound' | 'outbound';
  status: 'received' | 'processing' | 'processed' | 'transmitted' | 'error';
  invoiceType?: 'original' | 'BD' | 'CO' | 'CA';
  originalInvoiceNumber?: string;
  cdataTrackingId?: string;
  cdataConfirmationId?: string;
  loadNumber?: string;
  rawData?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EdiDocumentAttachment {
  id: string;
  organizationId: string;
  transactionId: string;
  loadId?: string;
  invoiceId?: string;
  documentType: 'bol' | 'pod' | 'rate_confirmation' | 'lumper_receipt' | 'accessorial_approval' | 'other';
  fileName: string;
  fileSize: number;
  s3Key: string;
  mimeType: string;
  createdAt: string;
}

export interface TransmissionSummary {
  received: number;
  processing: number;
  transmitted: number;
  error: number;
}

export interface EdiStatusMapping {
  id: string;
  partnerId: string;
  partnerName?: string;
  loadStatus: string;
  ediCode: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// ---------- Partner Hooks ----------

interface EdiPartnerFilters {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
}

export function useEdiPartners(filters: EdiPartnerFilters = {}) {
  const { page = 1, limit = 25, search, active } = filters;
  return useQuery({
    queryKey: ['edi-partners', { page, limit, search, active }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      if (active !== undefined) params.set('active', String(active));
      const res = await api.get<PaginatedResponse<EdiPartner>>(`/edi/partners?${params}`);
      return res.data;
    },
  });
}

// #2197 — per-partner EDI config (Enterprise Features → EDI per-partner editor).
export type EdiFeatureConfig = Record<string, unknown>;

export function useEdiPartnerFeatureConfig(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ['edi-partner-feature-config', partnerId],
    enabled: !!partnerId,
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: EdiFeatureConfig }>(
        `/edi/partners/${partnerId}/feature-config`,
      );
      return res.data.data;
    },
  });
}

export function useUpdateEdiPartnerFeatureConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partnerId, config }: { partnerId: string; config: EdiFeatureConfig }) => {
      const res = await api.patch<{ success: boolean; data: EdiFeatureConfig }>(
        `/edi/partners/${partnerId}/feature-config`,
        config,
      );
      return res.data.data;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['edi-partner-feature-config', v.partnerId] });
    },
  });
}

export function useEdiPartner(id: string | undefined) {
  return useQuery({
    queryKey: ['edi-partners', id],
    queryFn: async () => {
      const res = await api.get<{ data: EdiPartner }>(`/edi/partners/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateEdiPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<EdiPartner>) => {
      const res = await api.post<{ data: EdiPartner }>('/edi/partners', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-partners'] });
    },
  });
}

export function useUpdateEdiPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<EdiPartner> & { id: string }) => {
      const res = await api.patch<{ data: EdiPartner }>(`/edi/partners/${id}`, data);
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['edi-partners'] });
      queryClient.invalidateQueries({ queryKey: ['edi-partners', variables.id] });
    },
  });
}

// ---------- Transaction Hooks ----------

interface EdiTransactionFilters {
  page?: number;
  limit?: number;
  direction?: string;
  status?: string;
  transactionSet?: string;
  partnerId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useEdiTransactions(filters: EdiTransactionFilters = {}) {
  const { page = 1, limit = 25, direction, status, transactionSet, partnerId, dateFrom, dateTo } = filters;
  return useQuery({
    queryKey: ['edi-transactions', { page, limit, direction, status, transactionSet, partnerId, dateFrom, dateTo }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (direction) params.set('direction', direction);
      if (status) params.set('status', status);
      if (transactionSet) params.set('transactionSet', transactionSet);
      if (partnerId) params.set('partnerId', partnerId);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get<PaginatedResponse<EdiTransaction>>(`/edi/transactions?${params}`);
      return res.data;
    },
  });
}

export function useEdiTransaction(id: string | undefined) {
  return useQuery({
    queryKey: ['edi-transactions', id],
    queryFn: async () => {
      const res = await api.get<{ data: EdiTransaction }>(`/edi/transactions/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useProcessInbound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { rawData: string; partnerId: string }) => {
      const res = await api.post<{ data: EdiTransaction }>('/edi/transactions/inbound', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

export function useSendResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accepted }: { id: string; accepted: boolean }) => {
      const res = await api.post<{ data: EdiTransaction }>(`/edi/transactions/${id}/respond`, { accepted });
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['edi-transactions', variables.id] });
    },
  });
}

export function useSend214() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { loadId: string; statusCode: string }) => {
      const res = await api.post<{ data: EdiTransaction }>('/edi/transactions/outbound/214', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

export function useSend210() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { loadId: string }) => {
      const res = await api.post<{ data: EdiTransaction }>('/edi/transactions/outbound/210', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

// ---------- EDI Processing Hooks (WAYP-162) ----------

export interface ParsedEdiResult {
  transactionSet: string;
  parsed: Record<string, any>;
}

export function useParseEdi() {
  return useMutation({
    mutationFn: async (data: { content: string; tradingPartnerId?: string }) => {
      const res = await api.post<{ data: ParsedEdiResult }>('/edi/parse', data);
      return res.data.data;
    },
  });
}

export function useGenerateEdi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      type: '204' | '990' | '214' | '210';
      loadId: string;
      tradingPartnerId: string;
      accepted?: boolean;
      reasonCode?: string;
      statusCode?: string;
      city?: string;
      state?: string;
      stopNumber?: number;
      invoiceNumber?: string;
      totalCharges?: number;
      billingDate?: string;
    }) => {
      const { type, loadId, ...body } = data;
      const res = await api.post<{ data: { transaction: EdiTransaction; ediPayload: string } }>(
        `/edi/generate/${type}/${loadId}`,
        body
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

export function useProcessEdi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { content: string; tradingPartnerId: string; autoCreateLoad?: boolean }) => {
      const res = await api.post<{ data: { transaction: EdiTransaction; parsed: any; load: any } }>(
        '/edi/process',
        data
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['loads'] });
    },
  });
}

// ---------- Status Mapping Hooks ----------

export function useEdiStatusMappings(partnerId?: string) {
  return useQuery({
    queryKey: ['edi-status-mappings', partnerId],
    queryFn: async () => {
      const params = partnerId ? `?partnerId=${partnerId}` : '';
      const res = await api.get<{ data: EdiStatusMapping[] }>(`/edi/status-mappings${params}`);
      return res.data.data;
    },
  });
}

// ---------- WAYP-277: Transmission & Dashboard Hooks ----------

export function useTransmissionSummary(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['edi-transmission-summary', { dateFrom, dateTo }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const qs = params.toString() ? `?${params}` : '';
      const res = await api.get<{ data: TransmissionSummary }>(`/edi/transactions/summary${qs}`);
      return res.data.data;
    },
    refetchInterval: 30_000, // refresh every 30s for live counts
  });
}

export function useTransmitEdi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transactionId: string) => {
      const res = await api.post<{ data: { jobId: string } }>(
        `/edi/transactions/${transactionId}/transmit`,
        {}
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

export function useRetryTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transactionId: string) => {
      const res = await api.post<{ data: { jobId: string } }>(
        `/edi/transactions/${transactionId}/retry`,
        {}
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edi-transactions'] });
    },
  });
}

// ---------- WAYP-277: EDI Document Attachment Hooks ----------

export function useEdiDocuments(transactionId: string | undefined) {
  return useQuery({
    queryKey: ['edi-documents', transactionId],
    queryFn: async () => {
      const res = await api.get<{ data: EdiDocumentAttachment[] }>(
        `/edi/transactions/${transactionId}/documents`
      );
      return res.data.data;
    },
    enabled: !!transactionId,
  });
}

export function useAttachDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      transactionId: string;
      loadId?: string;
      invoiceId?: string;
      documentType: EdiDocumentAttachment['documentType'];
      fileName: string;
      fileSize: number;
      s3Key: string;
      mimeType: string;
    }) => {
      const res = await api.post<{ data: EdiDocumentAttachment }>('/edi/documents', data);
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['edi-documents', variables.transactionId] });
    },
  });
}

// ---------- FourKites Monitoring (D4) ----------

export interface FourkitesMonitoringSummary {
  documents: number;
  sent: number;
  failed: number;
  pending: number;
  latLongMissing: number;
  updates: number;
}

export interface FourkitesMonitoringLoadRow {
  loadId: string;
  loadDisplayId: string | null;
  ediTradingPartnerId: string;
  partnerName: string | null;
  scac: string | null;
  sendModes: string[];
  documents: number;
  sent: number;
  failed: number;
  pending: number;
  latLongMissing: number;
  updates: number;
  lastActivityAt: string | null;
}

interface FourkitesMonitoringResponse {
  summary: FourkitesMonitoringSummary;
  data: FourkitesMonitoringLoadRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface FourkitesMonitoringFilters {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  partnerId?: string;
  status?: 'pending' | 'sent' | 'failed';
  latLongMissing?: boolean;
}

export interface FourkitesEnabledPartner {
  id: string;
  name: string;
  scacCode: string | null;
}

/**
 * The org's active FourKites-enabled trading partners. Powers both the
 * USPS Carrier > Loads nav gate (entry hidden when empty) and the monitoring
 * page's partner selector.
 */
export function useFourkitesPartners() {
  return useQuery({
    queryKey: ['fourkites-partners'],
    queryFn: async () => {
      const res = await api.get<{ data: FourkitesEnabledPartner[] }>('/edi/fourkites/partners');
      return res.data.data;
    },
    staleTime: 60_000,
  });
}

export function useFourkitesMonitoring(filters: FourkitesMonitoringFilters = {}) {
  const { page = 1, limit = 25, dateFrom, dateTo, partnerId, status, latLongMissing } = filters;
  return useQuery({
    queryKey: ['fourkites-monitoring', { page, limit, dateFrom, dateTo, partnerId, status, latLongMissing }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (partnerId) params.set('partnerId', partnerId);
      if (status) params.set('status', status);
      if (latLongMissing) params.set('latLongMissing', 'true');
      const res = await api.get<FourkitesMonitoringResponse>(`/edi/fourkites/monitoring?${params}`);
      return res.data;
    },
    refetchInterval: 30_000,
  });
}

// ---------- WAYP-277: Partner Go-Live Hooks ----------

export function usePartnerGoLive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partnerId: string) => {
      const res = await api.post<{ data: EdiPartner }>(
        `/edi/partners/${partnerId}/go-live`,
        {}
      );
      return res.data.data;
    },
    onSuccess: (_data, partnerId) => {
      queryClient.invalidateQueries({ queryKey: ['edi-partners'] });
      queryClient.invalidateQueries({ queryKey: ['edi-partners', partnerId] });
    },
  });
}

export function usePartnerSuspend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ partnerId, reason }: { partnerId: string; reason?: string }) => {
      const res = await api.post<{ data: EdiPartner }>(
        `/edi/partners/${partnerId}/suspend`,
        { reason }
      );
      return res.data.data;
    },
    onSuccess: (_data, { partnerId }) => {
      queryClient.invalidateQueries({ queryKey: ['edi-partners'] });
      queryClient.invalidateQueries({ queryKey: ['edi-partners', partnerId] });
    },
  });
}

export function useUpdateGoLiveChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      partnerId,
      updates,
    }: {
      partnerId: string;
      updates: Partial<GoLiveChecklist>;
    }) => {
      const res = await api.patch<{ data: EdiPartner }>(
        `/edi/partners/${partnerId}/checklist`,
        updates
      );
      return res.data.data;
    },
    onSuccess: (_data, { partnerId }) => {
      queryClient.invalidateQueries({ queryKey: ['edi-partners', partnerId] });
    },
  });
}
